import type { ComponentProps } from 'react'
import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import TasteOfGoldLearnMoreModal from './TasteOfGoldLearnMoreModal'
import { TasteOfGoldGoldLabel, TasteOfGoldIconCircle } from './TasteOfGoldPromoStyles'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'

type BodyTextVariant = ComponentProps<typeof BodyText>['variant']
type TextLinkSize = ComponentProps<typeof TextLink>['size']

interface Props {
  /**
   * Controls the body text and link size.
   * @default 'small'
   */
  size?: BodyTextVariant
  /**
   * Layout variant for the promo content.
   * - 'vertical': label on top, icon on the right (used in drawer)
   * - 'horizontal': icon on left, label inline with title (used on landing page)
   * @default 'vertical'
   */
  layout?: 'vertical' | 'horizontal'
  /**
   * Whether to stop click propagation on the Details link.
   * Needed when rendered inside a parent Clickable (e.g. account drawer).
   */
  stopPropagation?: boolean
  /**
   * Controls the description copy variant.
   * - 'general': "...for your next international escape." (default, used on loyalty pages)
   * - 'offerPage': "...when you book an eligible package." (used on offer pages)
   * @default 'general'
   */
  context?: 'general' | 'offerPage'
}

const BODY_TO_LINK_SIZE: Record<string, TextLinkSize> = {
  small: 'small',
  medium: 'medium',
  large: 'large',
}

function TasteOfGoldPromoContent(props: Props) {
  const {
    size = 'small',
    layout = 'vertical',
    stopPropagation = false,
    context = 'general',
  } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const linkSize = BODY_TO_LINK_SIZE[size] ?? 'small'

  const openLearnMore = useCallback((event: React.MouseEvent) => {
    if (stopPropagation) {
      event.preventDefault()
      event.stopPropagation()
    }
    showModal(<TasteOfGoldLearnMoreModal />)
  }, [showModal, stopPropagation])

  const specialGiftLabel = intl.formatMessage({
    id: '+Py9KNN62H',
    defaultMessage: 'A Special Gift For You',
    description: 'Bold heading for Taste of Gold promotional content',
  })
  const limitedTimeLabel = intl.formatMessage({
    id: 'tsaNVCmWTU',
    defaultMessage: 'Limited time only',
    description: 'Gold label for Taste of Gold limited time promotion',
  })

  const offerPageDescription = <FormattedMessage
    id="4TYYgUllK7"
    defaultMessage="You’re invited to experience {program} {tier}. Enjoy a free airport transfer when you book an eligible package. <link>Details</link>"
    description="Offer page promo text for Taste of Gold campaign with details link"
    values={{
      program: getLoyaltyProgramDisplayName(),
      tier: getLoyaltyTierDisplayName('gold'),
      link: (chunks) => <TextLink size={linkSize} underline onClick={openLearnMore}>{chunks}</TextLink>,
    }}
  />
  const generalDescription = <FormattedMessage
    id="ky/kfHWu/d"
    defaultMessage="You’re invited to experience {program} {tier}. Enjoy a free airport transfer for your next international escape. <link>Details</link>"
    description="General promo text for Taste of Gold campaign with details link"
    values={{
      program: getLoyaltyProgramDisplayName(),
      tier: getLoyaltyTierDisplayName('gold'),
      link: (chunks) => <TextLink size={linkSize} underline onClick={openLearnMore}>{chunks}</TextLink>,
    }}
  />
  const description = context === 'offerPage' ? offerPageDescription : generalDescription

  const textContent = (
    <VerticalSpacer gap={4}>
      {layout === 'horizontal' && <Group direction="horizontal" verticalAlign="center" gap={8} wrap="wrap">
        <BodyText variant="medium" weight="bold">{specialGiftLabel}</BodyText>
        <TasteOfGoldGoldLabel>{limitedTimeLabel}</TasteOfGoldGoldLabel>
      </Group>}
      {layout !== 'horizontal' && <BodyText variant="medium" weight="bold">{specialGiftLabel}</BodyText>}
      <BodyText variant={size}>
        {description}
      </BodyText>
    </VerticalSpacer>
  )

  if (layout === 'horizontal') {
    return (
      <Group direction="horizontal" verticalAlign="start" gap={12}>
        <TasteOfGoldIconCircle>
          <span><LineTaxiIcon size="M" /></span>
        </TasteOfGoldIconCircle>
        {textContent}
      </Group>
    )
  }

  return (
    <VerticalSpacer gap={8}>
      <div><TasteOfGoldGoldLabel>{limitedTimeLabel}</TasteOfGoldGoldLabel></div>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
        {textContent}
        <TasteOfGoldIconCircle>
          <span><LineTaxiIcon size="M" /></span>
        </TasteOfGoldIconCircle>
      </Group>
    </VerticalSpacer>
  )
}

export default TasteOfGoldPromoContent
